import type { CalendarProviders } from '../types/calendar';

/**
 * Settings Service
 *
 * Comprehensive settings management with validation and localStorage
 * persistence. This service is deliberately offline: it makes no API calls.
 * The subset of settings that follows the user across devices is synced
 * separately by `meetingPreferencesSync` and SettingsContext (SM-1168).
 */

import { merge } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { jitsiDefaultSettings } from '../types/jitsiDefaults';
import type { JitsiMeetingSettingKeys } from '../types/jitsiSettings';
import {
  DEVICE_LS_KEYS,
  getCurrentBrowserDevices,
  syncDeviceLsKeys,
  updateJitsiLocalStorageDeviceLabels,
  updateJitsiLocalStorageDevices,
  updateJitsiLocalStorageStartMuted,
} from '../utils/deviceUtils';

// Settings data schema (compatible with current Jitsi Meet)
export interface UserSettings {
  // Device settings
  devices: {
    selectedCamera: string;
    selectedMicrophone: string;
    selectedAudioOutput: string;
    startWithAudioMuted: boolean; // Start calls with audio muted (Jitsi local setting)
    startWithVideoMuted: boolean; // Start calls with video muted (Jitsi local setting)
  };

  // Profile settings
  profile: {
    defaultMeetingId: string;
    defaultMeetingPasscode: string;
    displayName: string;
    gravatarEmail: string;
  };

  // Calendar settings
  calendar: CalendarProviders;
  // Meeting preferences (compatible with current Jitsi Meet)
  meetings: {
    [key in JitsiMeetingSettingKeys]?: boolean | string | number;
  };

  // Terms and legal. The acceptance date itself is not kept here: it lives in
  // the backend `tos` document, which is the only writer (SM-1168).
  terms: {
    acceptedVersion: string;
  };

  // Metadata
  metadata: {
    version: string;
    lastSaved: string;
    lastSynced?: string;
  };
}

// Validation rules
export interface ValidationRule<T> {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: T) => string | null;
}

export interface ValidationSchema {
  [key: string]: ValidationRule<unknown> | ValidationSchema;
}

// Settings validation schema
export const settingsValidationSchema: ValidationSchema = {
  profile: {
    displayName: {
      required: true,
      minLength: 1,
      maxLength: 100,
      pattern: /^[a-zA-Z0-9\s\-_.]+$/,
      custom: (value: unknown) => {
        if (typeof value !== 'string') {
          return null;
        }
        if (value.trim().length === 0) {
          return 'Display name cannot be empty';
        }
        if (value.trim() !== value) {
          return 'Display name cannot have leading/trailing spaces';
        }
        return null;
      },
    },
    gravatarEmail: {
      required: false,
      maxLength: 254,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      custom: (value: unknown) => {
        if (typeof value !== 'string') {
          return null;
        }
        if (value && !value.includes('@')) {
          return 'Please enter a valid email address';
        }
        return null;
      },
    },
  },
  devices: {
    selectedCamera: { required: false },
    selectedMicrophone: { required: false },
    selectedAudioOutput: { required: false },
  },
  meetings: {
    ...jitsiDefaultSettings,
  } as ValidationSchema,
};

// Default settings
export const defaultSettings: UserSettings = {
  devices: {
    selectedCamera: '',
    selectedMicrophone: '',
    selectedAudioOutput: '',
    startWithAudioMuted: jitsiDefaultSettings.startWithAudioMuted as boolean,
    startWithVideoMuted: jitsiDefaultSettings.startWithVideoMuted as boolean,
  },
  profile: {
    defaultMeetingId: '',
    defaultMeetingPasscode: '',
    displayName: '',
    gravatarEmail: '',
  },
  calendar: {
    google: {
      connected: false,
      email: null,
      login_link: '',
    },
    microsoft: {
      connected: false,
      email: null,
      login_link: '',
    },
  },
  meetings: { ...jitsiDefaultSettings } as UserSettings['meetings'],
  terms: {
    acceptedVersion: '1.0',
  },
  metadata: {
    version: '1.0.0',
    lastSaved: new Date().toISOString(),
  },
};

// Validation errors
export interface ValidationErrors {
  [key: string]: string | ValidationErrors;
}

class SettingsService {
  private static instance: SettingsService;
  private settings: UserSettings = { ...defaultSettings };
  private listeners: Array<(settings: UserSettings) => void> = [];
  private saveTimeout: NodeJS.Timeout | null = null;
  private readonly storageKey = 'sangoma-meet-settings';
  /**
   * Sections edited in memory that localStorage has not seen yet. Callers that
   * stage edits (`persist: false`) rely on a later save to write them, and that
   * save has no updates of its own to derive the section list from (SM-1268).
   */
  private pendingSections = new Set<keyof UserSettings>();

  constructor() {
    this.loadSettings();
    this.setupStorageListener();
  }

  static getInstance(): SettingsService {
    if (!SettingsService.instance) {
      SettingsService.instance = new SettingsService();
    }
    return SettingsService.instance;
  }

  /**
   * Load settings from localStorage and merge with defaults
   */
  private loadSettings(): void {
    this.pendingSections.clear();
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsedSettings = JSON.parse(stored);
        this.settings = this.mergeWithDefaults(parsedSettings);
        syncDeviceLsKeys(this.settings.devices);
        console.log('[SettingsService] Loaded settings from localStorage:', this.settings);
      } else {
        this.settings = { ...defaultSettings };
        console.log('[SettingsService] Using default settings');
      }
    } catch (error) {
      console.error('[SettingsService] Error loading settings:', error);
      this.settings = { ...defaultSettings };
    }
  }

  /**
   * Merge stored settings with defaults to handle schema changes
   */
  private mergeWithDefaults(stored: Partial<UserSettings>): UserSettings {
    const merged = { ...defaultSettings };

    // Deep merge each section
    if (stored.devices) {
      merged.devices = { ...merged.devices, ...stored.devices };
    }
    if (stored.profile) {
      merged.profile = { ...merged.profile, ...stored.profile };
    }
    if (stored.calendar) {
      merged.calendar = { ...merged.calendar, ...stored.calendar };
    }
    if (stored.meetings) {
      merged.meetings = { ...merged.meetings, ...stored.meetings };
    }
    if (stored.terms) {
      merged.terms = { ...merged.terms, ...stored.terms };
    }
    if (stored.metadata) {
      merged.metadata = { ...merged.metadata, ...stored.metadata };
    }

    return merged;
  }

  /**
   * Listen for storage changes from other tabs
   */
  private setupStorageListener(): void {
    window.addEventListener('storage', (event) => {
      if (event.key === this.storageKey && event.newValue) {
        try {
          const newSettings = JSON.parse(event.newValue);
          this.settings = this.mergeWithDefaults(newSettings);
          this.notifyListeners();
          console.log('[SettingsService] Settings updated from another tab');
        } catch (error) {
          console.error('[SettingsService] Error parsing settings from storage event:', error);
        }
      }
    });
  }

  async setDevicesFromBrowser(): Promise<{ success: boolean }> {
    try {
      const browserDevices = await getCurrentBrowserDevices();
      await this.updateSettings({
        devices: {
          ...this.settings.devices,
          selectedCamera: browserDevices.selectedCamera,
          selectedMicrophone: browserDevices.selectedMicrophone,
          selectedAudioOutput: browserDevices.selectedAudioOutput,
        },
      });
      return { success: true };
    } catch {
      return { success: false };
    }
  }

  /**
   * Get current settings
   */
  getSettings(): UserSettings {
    return { ...this.settings };
  }

  /**
   * Revert in-memory settings to what is persisted in localStorage.
   * Use when user cancels without saving (e.g. in Settings modal).
   */
  revertToPersisted(): void {
    this.loadSettings();
    this.notifyListeners();
  }

  /**
   * Get specific setting by path (e.g., 'profile.displayName')
   */
  getSetting<T>(path: string): T | undefined {
    const keys = path.split('.');
    let value: unknown = this.settings;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = (value as Record<string, unknown>)[key];
      } else {
        return undefined;
      }
    }

    return value as T;
  }

  /**
   * Update settings with validation
   */
  async updateSettings(
    updates: Partial<UserSettings>,
    options: {
      validate?: boolean;
      persist?: boolean;
    } = {},
  ): Promise<{ success: boolean; errors?: ValidationErrors }> {
    const { validate = true, persist = true } = options;

    // Validate if requested
    if (validate) {
      const errors = this.validateSettings(updates);
      if (Object.keys(errors).length > 0) {
        return { success: false, errors };
      }
    }

    // Deep merge updates into current settings using lodash merge
    this.settings = merge({}, this.settings, updates);
    this.settings.metadata.lastSaved = new Date().toISOString();

    const sections = Object.keys(updates) as Array<keyof UserSettings>;

    if (persist) {
      // Anything staged earlier is written now too, otherwise it would stay
      // pending behind a save that only names its own sections.
      this.saveToLocalStorage([...new Set([...this.pendingSections, ...sections])]);
      this.pendingSections.clear();
    } else {
      sections.forEach((section) => {
        this.pendingSections.add(section);
      });
    }

    // Notify listeners
    this.notifyListeners();

    console.log('[SettingsService] Settings updated:', updates);
    return { success: true };
  }

  /**
   * Update a specific setting by path
   */
  async updateSetting<T>(
    path: string,
    value: T,
    options?: {
      validate?: boolean;
      persist?: boolean;
    },
  ): Promise<{ success: boolean; errors?: ValidationErrors }> {
    const [rootKey, restKey] = path.split(/\.(.+)/); // split only once
    const updates = { [rootKey]: { [restKey]: value } } as Partial<UserSettings>;
    return this.updateSettings(updates, options);
  }

  /**
   * Write the sections staged by earlier `persist: false` updates.
   *
   * This is what an explicit Save means for callers that edit in memory first:
   * they have no updates left to hand over, and passing an empty object would
   * persist only the refreshed metadata (SM-1268). Nothing pending is a no-op,
   * so saving an untouched form does not rewrite the stored blob.
   *
   * The staged edits were already validated when they were applied, so this
   * does not revalidate — doing so would reject documents (a blank display
   * name, say) that the user never touched in this session.
   */
  async flushPendingChanges(): Promise<{ success: boolean }> {
    if (this.pendingSections.size === 0) {
      return { success: true };
    }

    const sections = [...this.pendingSections];
    this.pendingSections.clear();
    this.settings.metadata.lastSaved = new Date().toISOString();
    this.saveToLocalStorage(sections);
    this.notifyListeners();

    console.log('[SettingsService] Flushed pending sections:', sections);
    return { success: true };
  }

  /**
   * Validate settings against schema
   */
  validateSettings(settings: Partial<UserSettings>): ValidationErrors {
    const errors: ValidationErrors = {};

    const validateObject = (
      obj: Record<string, unknown> | undefined,
      schema: ValidationSchema,
      path: string = '',
    ): void => {
      if (!obj) {
        return;
      }
      for (const [key, rule] of Object.entries(schema)) {
        const fullPath = path ? `${path}.${key}` : key;
        const value = obj[key];

        if (typeof rule === 'object' && rule !== null && !('required' in rule)) {
          // Nested schema
          if (value && typeof value === 'object') {
            validateObject(value as Record<string, unknown>, rule as ValidationSchema, fullPath);
          }
        } else {
          // Validation rule
          const validationRule = rule as ValidationRule<unknown>;
          const error = this.validateField(value, validationRule);
          if (error) {
            this.setNestedError(errors, fullPath, error);
          }
        }
      }
    };

    validateObject(settings as Record<string, unknown>, settingsValidationSchema);
    return errors;
  }

  /**
   * Validate individual field
   */
  private validateField(value: unknown, rule: ValidationRule<unknown>): string | null {
    // Required check
    if (rule.required && (value === undefined || value === null || value === '')) {
      return 'This field is required';
    }

    // Skip other validations if value is empty and not required
    if (!rule.required && (value === undefined || value === null || value === '')) {
      return null;
    }

    // String validations
    if (typeof value === 'string') {
      if (rule.minLength && value.length < rule.minLength) {
        return `Must be at least ${rule.minLength} characters`;
      }
      if (rule.maxLength && value.length > rule.maxLength) {
        return `Must be no more than ${rule.maxLength} characters`;
      }
      if (rule.pattern && !rule.pattern.test(value)) {
        return 'Invalid format';
      }
    }

    // Custom validation
    if (rule.custom) {
      return rule.custom(value);
    }

    return null;
  }

  /**
   * Set nested error in errors object
   */
  private setNestedError(errors: ValidationErrors, path: string, error: string): void {
    const keys = path.split('.');
    let current = errors;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current)) {
        current[key] = {};
      }
      current = current[key] as ValidationErrors;
    }

    current[keys[keys.length - 1]] = error;
  }

  /**
   * Sections this save is not responsible for keep whatever is already
   * persisted. `loadSettings()` runs once per instance and the `storage` event
   * never fires for writes made by our own window, so `this.settings` can hold
   * a stale copy of a section some other writer changed directly (the meeting
   * popup bridge and the E2E suite both write `sangoma-meet-settings` that way).
   * Serializing the whole in-memory object would silently revert those.
   *
   * `sections` undefined means a deliberate full overwrite; an empty array
   * writes only the refreshed metadata.
   */
  private buildPersistPayload(sections?: Array<keyof UserSettings>): unknown {
    if (!sections) {
      return this.settings;
    }

    let persisted: Partial<UserSettings>;
    try {
      const raw = localStorage.getItem(this.storageKey);
      persisted = raw ? JSON.parse(raw) : {};
    } catch {
      // Unreadable persisted blob — nothing worth preserving, write it all.
      return this.settings;
    }

    const payload: Record<string, unknown> = { ...persisted };
    for (const section of [...sections, 'metadata' as const]) {
      payload[section] = this.settings[section];
    }
    return payload;
  }

  /**
   * Save settings to localStorage, sync device keys (DEVICE_LS_KEYS), and update jitsiLocalStorage (features/base/settings).
   */
  private saveToLocalStorage(sections?: Array<keyof UserSettings>): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.buildPersistPayload(sections)));

      // Mirroring devices when this write did not persist them would leave the
      // flat keys ahead of the stored blob, and `loadSettings` copies the blob
      // back over them on the next reload — the selection would appear to save
      // and then revert.
      if (sections && !sections.includes('devices')) {
        console.log('[SettingsService] Settings saved to localStorage');
        return;
      }

      const d = this.settings.devices;
      localStorage.setItem(DEVICE_LS_KEYS.camera, d.selectedCamera ?? '');
      localStorage.setItem(DEVICE_LS_KEYS.microphone, d.selectedMicrophone ?? '');
      localStorage.setItem(DEVICE_LS_KEYS.audioOutput, d.selectedAudioOutput ?? '');
      updateJitsiLocalStorageDevices({
        selectedCamera: d.selectedCamera,
        selectedMicrophone: d.selectedMicrophone,
        selectedAudioOutput: d.selectedAudioOutput,
      });
      updateJitsiLocalStorageStartMuted({
        startWithAudioMuted: !!d.startWithAudioMuted,
        startWithVideoMuted: !!d.startWithVideoMuted,
      });
      // Best-effort, fire-and-forget: keep device labels fresh alongside IDs on
      // every save path (not just the Settings modal's explicit Save), so stale
      // deviceId recovery (SM-1151) always has a label to match against.
      void updateJitsiLocalStorageDeviceLabels({
        selectedCamera: d.selectedCamera,
        selectedMicrophone: d.selectedMicrophone,
        selectedAudioOutput: d.selectedAudioOutput,
      });
      console.log('[SettingsService] Settings saved to localStorage');
    } catch (error) {
      console.error('[SettingsService] Error saving to localStorage:', error);
    }
  }

  /**
   * Reset settings to defaults
   */
  async resetToDefaults(): Promise<void> {
    this.pendingSections.clear();
    this.settings = { ...defaultSettings };
    this.settings.metadata.lastSaved = new Date().toISOString();
    this.saveToLocalStorage();
    this.notifyListeners();
    console.log('[SettingsService] Settings reset to defaults');
  }

  /**
   * Export settings for backup
   */
  exportSettings(): string {
    return JSON.stringify(this.settings, null, 2);
  }

  /**
   * Import settings from backup
   */
  async importSettings(
    settingsJson: string,
  ): Promise<{ success: boolean; errors?: ValidationErrors }> {
    try {
      const importedSettings = JSON.parse(settingsJson);
      return await this.updateSettings(importedSettings, {
        validate: true,
        persist: true,
      });
    } catch (_error) {
      return {
        success: false,
        errors: { import: 'Invalid settings format' },
      };
    }
  }

  /**
   * Subscribe to settings changes
   */
  subscribe(listener: (settings: UserSettings) => void): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of settings changes
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.settings);
      } catch (error) {
        console.error('[SettingsService] Error in settings listener:', error);
      }
    });
  }

  /**
   * Debounced save to prevent excessive saves
   */
  debouncedSave(delay: number = 1000): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    this.saveTimeout = setTimeout(() => {
      this.saveToLocalStorage();
    }, delay);
  }

  /**
   * Get validation errors for current settings
   */
  getCurrentValidationErrors(): ValidationErrors {
    return this.validateSettings(this.settings);
  }

  /**
   * Check if settings are valid
   */
  isValid(): boolean {
    const errors = this.getCurrentValidationErrors();
    return Object.keys(errors).length === 0;
  }
}

// Export singleton instance
export const settingsService = SettingsService.getInstance();

// React hook for using settings
export const useSettings = () => {
  const [settings, setSettings] = useState(settingsService.getSettings());
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const unsubscribe = settingsService.subscribe(setSettings);
    return unsubscribe;
  }, []);

  const updateSettings = useCallback(
    async (updates: Partial<UserSettings>, options?: { validate?: boolean; persist?: boolean }) => {
      setIsLoading(true);
      setErrors({});

      try {
        const result = await settingsService.updateSettings(updates, options);
        if (!result.success && result.errors) {
          setErrors(result.errors);
        }
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const updateSetting = useCallback(
    async <T>(path: string, value: T, options?: { validate?: boolean; persist?: boolean }) => {
      setIsLoading(true);
      setErrors({});

      try {
        const result = await settingsService.updateSetting(path, value, options);
        if (!result.success && result.errors) {
          setErrors(result.errors);
        }
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const resetSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      await settingsService.resetToDefaults();
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    settings,
    isLoading,
    errors,
    updateSettings,
    updateSetting,
    resetSettings,
    isValid: settingsService.isValid(),
    exportSettings: settingsService.exportSettings.bind(settingsService),
    importSettings: settingsService.importSettings.bind(settingsService),
  };
};

export default settingsService;
